import { useEffect, useState } from "react";
import { Modal } from "components/modal/Modal";
import { useTranslation } from "react-i18next";
import { downloadFromOrgFiles } from "integrations/firebase/storage";
import { logError } from "shared/services/ErrorReporting";
import { toast } from "utils/toast";

import { IconProps } from "components/icon/Icon";
import { OnixGenerationModalStatus } from "pages/ProjectDetails/types/types";
import { GeneratingSkeleton } from "./GeneratingSkeleton";
import { getOnixExportData } from "./utils";

export const OnixGenerationModal = ({
  showOnixExportModal,
  setShowOnixExportModal,
  generatingStatus,
  setGeneratingStatus,
  onixUrl,
}: {
  showOnixExportModal: boolean;
  setShowOnixExportModal: (show: boolean) => void;
  setGeneratingStatus: (status: OnixGenerationModalStatus) => void;
  generatingStatus: OnixGenerationModalStatus;
  onixUrl?: string | null;
}) => {
  const { t } = useTranslation(["projectDetails"]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [displayStatus, setDisplayStatus] = useState(generatingStatus);

  const {
    title,
    description,
    cancelLabel,
    confirmLabel,
    variant,
    confirmIconName,
  } = getOnixExportData(t, displayStatus);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setShowOnixExportModal(false);
      setGeneratingStatus(OnixGenerationModalStatus.IDLE);
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    if (!showOnixExportModal) return;
    // mirrors the live generation status into the modal only while it is open, freezing the last status during the close animation; the freeze cannot be expressed as a pure derivation
    setDisplayStatus(generatingStatus);
  }, [showOnixExportModal, generatingStatus]);

  const canDownload =
    generatingStatus === OnixGenerationModalStatus.SUCCESS && Boolean(onixUrl);

  const handleConfirm = async () => {
    if (isDownloading || !canDownload || !onixUrl) return;
    setIsDownloading(true);
    try {
      await downloadFromOrgFiles(onixUrl, "onix-export.zip");
    } catch (e) {
      logError(e as Error);
      toast.error(t("projectDetails:toast.errorDownloadOnix"));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Modal
      id="onix-generation"
      variant={variant}
      title={title}
      description={description}
      cancelLabel={cancelLabel}
      confirmLabel={confirmLabel}
      confirmIconName={confirmIconName as IconProps["name"]}
      cancelIconName="x"
      open={showOnixExportModal}
      onConfirm={handleConfirm}
      onOpenChange={handleOpenChange}
      loading={isDownloading}
      disabledConfirm={!canDownload}
    >
      {onixUrl ? (
        <span data-testid="onix-filename" className="sr-only">
          {onixUrl.split("/").pop()}
        </span>
      ) : null}
      {displayStatus === OnixGenerationModalStatus.REQUESTED ? (
        <GeneratingSkeleton />
      ) : null}
    </Modal>
  );
};
